/* eslint-disable */

'use client'

import { useEffect, useMemo, useState } from 'react'
import { Box, Button, ButtonGroup } from '@mui/material'
import { type DocumentRendererData } from '@components/common'
import { CustomStyles, getStyles } from '@utils/styles'
import defaultStyles, { StyleClassNames } from './styles'

export type ContentSectionData = {
  docRendererData?: DocumentRendererData
  name: string
  maxHeight?: { xs: number; sm: number; md: number }
  noFrame?: boolean
  noTab?: boolean
  position: number
}

type TabPanelSectionProps = {
  items: ContentSectionData[]
  compact?: boolean
  csx?: CustomStyles<StyleClassNames>
  /**
   * When set, scroll detection uses an offset-aware strategy:
   * the active tab becomes the LAST section whose top edge has scrolled
   * above this offset (px from viewport top). Click-to-scroll also
   * positions the section at this offset. Leave undefined for the
   * default "first section with top >= 0" behaviour.
   */
  scrollOffset?: number
}

export function TabPanelSection({
  items,
  compact = true,
  csx,
  scrollOffset,
}: TabPanelSectionProps) {
  const styles = getStyles(defaultStyles, csx)
  const [selected, setSelected] = useState(items[0]?.name || '')

  const tabs = useMemo(
    () => items.filter((i) => !i.noFrame && !i.noTab),
    [items]
  )

  const handleScrollTo = (name: string) => {
    const element = document.getElementById(name)
    if (element) {
      if (scrollOffset != null) {
        // Scroll so the section lands just below the sticky header
        const elementTop = element.getBoundingClientRect().top + window.scrollY
        window.scrollTo({
          top: elementTop - scrollOffset + 16, // scroll slightly past the offset so the scroll handler detects it
          behavior: 'smooth',
        })
      } else {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  }

  useEffect(() => {
    const handleScroll = () => {
      const sections = tabs.map((item) => document.getElementById(item.name))

      if (scrollOffset != null) {
        // Offset-aware: pick the LAST section whose top has scrolled above the offset line
        let activeSection: HTMLElement | null = null
        for (const section of sections) {
          if (!section) continue
          const rect = section.getBoundingClientRect()
          if (rect.top <= scrollOffset) {
            activeSection = section
          }
        }
        if (activeSection) {
          setSelected(activeSection.id)
        }
      } else {
        // Default: first section with top >= 0
        const sectionInView = sections.find((section) => {
          if (!section) return false
          const rect = section.getBoundingClientRect()
          return rect.top >= 0
        })
        if (sectionInView) {
          setSelected(sectionInView.id)
        }
      }
    }

    document.addEventListener('scroll', handleScroll)
    return () => {
      document.removeEventListener('scroll', handleScroll)
    }
  }, [tabs, scrollOffset])

  return (
    <Box
      {...styles('wrapper', {
        ...(compact && {
          paddingTop: '1rem',
          paddingBottom: '1rem',
        }),
        ...(!compact && {
          padding: '24px 6.5% 12px 6.5% ',
        }),
      })}
    >
      <Box
        {...styles('overflowBoxWrapper')}
        ref={(el: HTMLDivElement) => {
          if (el) {
            const selectedButton = el.querySelector(
              `[data-selected="true"]`
            ) as HTMLElement
            if (selectedButton) {
              const container = el
              const scrollLeft =
                selectedButton.offsetLeft -
                (container.clientWidth - selectedButton.offsetWidth) / 2
              container.scrollTo({ left: scrollLeft, behavior: 'smooth' })
            }
          }
        }}
      >
        <ButtonGroup {...styles('buttonGroup')}>
          {tabs
            .filter(Boolean)
            .filter((item) => !item.noFrame)
            .map((tab, idx) => (
              <Button
                key={idx}
                data-selected={selected === tab.name}
                onClick={() => {
                  setSelected(tab.name)
                  handleScrollTo(tab.name)
                }}
                sx={{
                  fontWeight: 500,
                  flex: 1,
                }}
              >
                {tab.name.replaceAll('-', ' ')}
              </Button>
            ))}
        </ButtonGroup>
      </Box>
    </Box>
  )
}
